/**
 * Extraction activity in the app shell.
 *
 * Extractions outlive the page they were started from, so the shell carries a
 * single compact chip: is anything running, did something finish that you have
 * not dismissed, and is anything waiting on you. It is a navigator, not a
 * second progress bar — the ledger on the source grid and the Documents page
 * stays the record, and this chip's job is to get you back to it.
 *
 * It watches the same project-wide store as the ledgers, so every surface on a
 * page shares one poll.
 */
import { AlertCircleIcon, ArrowUpRightIcon, XIcon } from 'lucide-react';
import { useContext, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useExtractionRuns } from '@/hooks/use-extraction-runs';
import { ProjectCtx } from '@/hooks/project-context';
import { TenantCtx } from '@/hooks/tenant-context';
import {
  describeExtractionActivity,
  extractionActivityToneLabel,
  summariseExtractionActivity,
} from '@/lib/extraction-runs/extraction-activity-summary';
import {
  describeExtractionRunProgress,
  extractionRunStateLabel,
} from '@/lib/extraction-runs/extraction-run-presentation';
import {
  extractionRunStore,
  isTerminalExtractionRunPhase,
  type ExtractionRun,
} from '@/lib/extraction-runs/extraction-run-store';
import { projectTemplateResultPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

import { EXTRACTION_PHASE_GLYPHS, EXTRACTION_TONE_GLYPHS } from './extraction-run-glyphs';

interface ActivityRunProps {
  readonly run: ExtractionRun;
  readonly tenantSlug: string | null;
  readonly onNavigate: () => void;
}

function ActivityRun({ run, tenantSlug, onNavigate }: ActivityRunProps): ReactNode {
  const glyph = EXTRACTION_PHASE_GLYPHS[run.phase];
  const resultHref =
    tenantSlug !== null && run.templateId !== null
      ? projectTemplateResultPath(tenantSlug, run.projectId, run.templateId)
      : null;

  return (
    <li
      data-testid="extraction-activity-run"
      data-run-id={run.runId}
      data-phase={run.phase}
      className="flex items-start gap-2.5 px-3 py-2.5"
    >
      <glyph.Icon
        aria-hidden="true"
        className={cn(
          'mt-0.5 size-4 shrink-0',
          glyph.className,
          glyph.spin && 'motion-safe:animate-spin',
        )}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-normal tracking-[-0.01em] text-foreground">
          {run.gridLabel}
        </span>
        <p className="text-xs font-light tabular-nums text-muted-foreground">
          {extractionRunStateLabel(run)} — {describeExtractionRunProgress(run)}
        </p>
        {run.failureMessage === null ? null : (
          <p className="text-xs font-light text-destructive">{run.failureMessage}</p>
        )}
        {run.acknowledgementError === null ? null : (
          <p className="text-xs font-light text-destructive">{run.acknowledgementError}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {resultHref === null ? null : (
          <Button asChild size="xs" variant="ghost">
            <NavLink to={resultHref} onClick={onNavigate}>
              <ArrowUpRightIcon data-icon="inline-start" aria-hidden="true" />
              Open result
            </NavLink>
          </Button>
        )}
        {/* Only a settled run can be cleared: nothing in flight has an outcome
            to acknowledge yet, and the server refuses the write anyway. */}
        {isTerminalExtractionRunPhase(run.phase) ? (
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={`Dismiss extraction activity for ${run.gridLabel}`}
            onClick={() => {
              extractionRunStore.acknowledge(run.projectId, run.runId);
            }}
          >
            <XIcon data-icon="standalone" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export function ExtractionActivityIndicator(): ReactNode {
  const projectContext = useContext(ProjectCtx);
  const tenant = useContext(TenantCtx);
  const [isOpen, setIsOpen] = useState(false);
  const projectId = projectContext?.project.project_id ?? null;
  const { runs, progressUnavailable } = useExtractionRuns(projectId, null);
  const summary = summariseExtractionActivity(runs);

  // Outside a project there is no unambiguous ledger to summarise, and with
  // nothing to report the shell stays quiet rather than showing a zero.
  if (projectId === null) {
    return null;
  }
  if (summary.tone === null && !progressUnavailable && !isOpen) {
    return null;
  }

  const tone = summary.tone;
  const glyph = tone === null ? EXTRACTION_TONE_GLYPHS.ready : EXTRACTION_TONE_GLYPHS[tone];
  const sentence = describeExtractionActivity(summary);

  return (
    <>
      {/* Aggregate, not per-run: the ledger surfaces narrate individual rows. */}
      <p role="status" className="sr-only">
        {sentence}
      </p>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={tone === 'ready' ? 'secondary' : 'outline'}
            size="sm"
            data-testid="extraction-activity-trigger"
            data-tone={tone ?? 'idle'}
            aria-label={`Extraction activity: ${sentence}`}
            className="h-11 rounded-full px-3"
          >
            {/* Keyed on tone so a settling run gets one authored beat: the
                spinner gives way to the outcome glyph. */}
            <glyph.Icon
              key={tone ?? 'idle'}
              data-icon="inline-start"
              aria-hidden="true"
              className={cn(
                glyph.className,
                glyph.spin ? 'motion-safe:animate-spin' : 'animate-ca-pop-in',
              )}
            />
            {tone === null ? null : (
              <span className="text-sm tabular-nums">{String(summary.count)}</span>
            )}
            <span
              className={cn(
                // Yields in a narrow app header like Sean's label, beside it.
                'hidden font-normal sm:inline @max-[24rem]/header:hidden',
                tone === 'attention' && 'text-warning',
              )}
            >
              {tone === null ? 'Activity' : extractionActivityToneLabel(tone)}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[min(24rem,calc(100vw-1.5rem))] gap-0 p-0"
          aria-label="Extraction activity"
        >
          <PopoverHeader className="px-3 pt-3 pb-2">
            <div className="flex items-center justify-between gap-2">
              <PopoverTitle className="text-sm">Extraction activity</PopoverTitle>
              {summary.ready.length > 0 ? (
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label={`Clear ${String(summary.ready.length)} completed ${summary.ready.length === 1 ? 'extraction' : 'extractions'}`}
                  onClick={() => {
                    extractionRunStore.acknowledgeSucceeded(projectId);
                  }}
                >
                  Clear completed
                </Button>
              ) : null}
            </div>
            <p className="text-xs font-light text-muted-foreground">{sentence}</p>
          </PopoverHeader>
          {summary.runs.length === 0 ? null : (
            <ul className="flex max-h-[min(60vh,24rem)] flex-col divide-y divide-border overflow-y-auto border-t border-border [scrollbar-gutter:stable]">
              {summary.runs.map((run) => (
                <ActivityRun
                  key={run.runId}
                  run={run}
                  tenantSlug={tenant?.slug ?? null}
                  onNavigate={() => {
                    setIsOpen(false);
                  }}
                />
              ))}
            </ul>
          )}
          {progressUnavailable ? (
            <div
              data-testid="extraction-activity-unavailable"
              className="flex items-start gap-2.5 border-t border-border px-3 py-2.5"
            >
              <AlertCircleIcon
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              />
              <p className="flex-1 text-xs font-light text-muted-foreground">
                Extraction progress could not be read. Any extraction you started may still be
                running.
              </p>
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label="Dismiss extraction progress warning"
                onClick={() => {
                  extractionRunStore.dismissProgressNotice(projectId);
                }}
              >
                <XIcon data-icon="standalone" aria-hidden="true" />
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </>
  );
}
